import { saveAs } from "file-saver";
import dayjs from "dayjs";
import type { VideoData } from "../shared/interfaces/VideoData";
import type { TranscriptData } from "../shared/interfaces/TranscriptData";
import { translateTranscripts } from "../api/transcriptTranslationApi";
import { NATIVE_DOWNLOAD_LANGUAGE } from "../shared/constants/downloadLanguages";

function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function hasAnyTranscript(
  videos: VideoData[] | null | undefined,
): boolean {
  if (!videos) return false;
  return videos.some((v) => !!v?.transcript);
}

function buildSingleVideoSection(
  video: VideoData,
  transcriptOverride?: TranscriptData,
): string[] {
  if (!video.transcript) return [];
  const transcriptData =
    transcriptOverride ?? (video.transcript as TranscriptData);
  const lines: string[] = [];
  if (video.title) lines.push(`Title: ${video.title}`);
  if (video.id) lines.push(`ID: ${video.id}`);
  if (video.recordedOn) {
    lines.push(
      `Recorded On: ${dayjs(video.recordedOn).format("MMM D, YYYY h:mm A")}`,
    );
  }
  if (video.recordedBy) lines.push(`Recorded By: ${video.recordedBy}`);
  lines.push("");
  for (const p of transcriptData.paragraphs ?? []) {
    lines.push(
      `[${formatTimestamp(p.start)} - ${formatTimestamp(p.end)}] Speaker ${p.speaker}`,
    );
    lines.push(p.sentences.map((s) => s.text).join(" "));
    lines.push("");
  }
  return lines;
}

export function downloadCombinedTranscript(
  videos: VideoData[] | null | undefined,
  filenameBase: string,
  translatedById?: Record<string, TranscriptData>,
): void {
  if (!videos) return;
  const sections = videos
    .map((v) => buildSingleVideoSection(v, translatedById?.[v.id]))
    .filter((s) => s.length > 0);
  if (sections.length === 0) return;
  const body = sections
    .map((s, i) => `===== Video ${i + 1} =====\n${s.join("\n")}`)
    .join("\n");
  const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
  saveAs(blob, `${filenameBase}.txt`);
}

/**
 * Downloads the combined transcript translated into the given language. The
 * native-language option uses the stored transcripts directly; every other
 * language (including English) fetches translations first, since transcripts
 * are not necessarily stored in English. Errors propagate to the caller.
 */
export async function downloadCombinedTranscriptInLanguage(
  videos: VideoData[] | null | undefined,
  filenameBase: string,
  languageCode: string,
): Promise<void> {
  if (!videos) return;
  if (languageCode === NATIVE_DOWNLOAD_LANGUAGE) {
    downloadCombinedTranscript(videos, `${filenameBase}-${languageCode}`);
    return;
  }
  const evidenceIds = videos.filter((v) => v?.transcript).map((v) => v.id);
  if (evidenceIds.length === 0) return;
  const translatedById = await translateTranscripts(evidenceIds, languageCode);
  downloadCombinedTranscript(
    videos,
    `${filenameBase}-${languageCode}`,
    translatedById,
  );
}
